import { forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";
import { v4 as uuidv4 } from "uuid";
import { useEffect, useRef, useState } from "react";
import OrdersGridCard from "../gridCards/OrdersGridCard";
import PremiumOrdersGridCard from "../gridCards/PremiumOrdersGridCard";
import * as Library from "../../../utils/Library";
import { AppQueryClient } from "../../../api/queryClient";
import { checkContextKey, listingCheckKey, reconcileCheckState } from "../../pages/orders/ordersComponents/automaticCheckState";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;

// #endregion

export default forwardRef(function InfinityGridOrdersCards(props, ref) {
    // Only the Orders page opts in; shared Archive callers keep their existing card.
    const usePremiumSummary = props?.usePremiumSummary === true;
    const OrderCard = usePremiumSummary ? PremiumOrdersGridCard : OrdersGridCard;
    const minimumColumnWidth = props?.minColumnWidth || "395px";
    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: usePremiumSummary
            ? `repeat(auto-fill, minmax(min(100%, ${minimumColumnWidth}), 1fr))`
            : `repeat(auto-fill, minmax(${minimumColumnWidth}, 1fr))`,
        gap: "24px",
        rowGap: usePremiumSummary ? "32px" : "24px"
    };

    // #region STATES
    const scrollContainerRef = useRef(null);
    const isArchivePage = props?.isArchivePage || false;
    const [scroll, setScroll] = useState(true);
    const pageLimit = props?.limit || 12;
    const [minimumCardsFill, setminimumCardsFill] = useState(1); //props?.minimumCardsFill || pageLimit

    const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());

    useEffect(() => {
        if (props?.queryKey) setQueryKey(props?.queryKey);
    }, [props?.queryKey]);

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getBulkOrdersData",
        {
            companyID: localStorage.getItem("currentCompanyID"),
            page: 1,
            limit: 50,
            filters: props?.filters
        },
        () => {
            setScroll(true);
        },
        true,
        "SupplierOrders"
    );

    //let images = [icon_pos_front_systems, icon_pos_sitoo, icon_pos_traze];

    const [checkStates, setCheckStates] = useState({});
    const rowObserver = useRef(props?.onAutomaticCheckRowsChange);
    rowObserver.current = props?.onAutomaticCheckRowsChange;
    const polling = useRef(false), pollCursor = useRef(0), loadedRows = useRef([]);
    loadedRows.current = (data?.pages || []).flatMap(page => page?.data?.result || []).filter(row => row?.automaticCheck?.enabled);
    const pageList = data?.pages;
    useEffect(() => {
        rowObserver.current?.((data?.pages || []).flatMap(page => page?.data?.result || [])
            .map(row => ({ ...row, automaticCheck: reconcileCheckState(row, checkStates[row._id]) })));
    }, [data?.pages, checkStates]);
    useEffect(() => {
        let active = true;
        const poll = async () => {
            if (polling.current || !loadedRows.current.length) return;
            polling.current = true;
            const all = loadedRows.current, start = pollCursor.current % all.length;
            const rows = Array.from({ length: Math.min(50, all.length) }, (_, i) => all[(start + i) % all.length]);
            pollCursor.current = (start + rows.length) % all.length;
            try {
                for (const row of rows) {
                    if (!active) break;
                    try {
                    const response = await Library.makeApiRequest({ endpoint: "SupplierOrders/getSupplierOrderCheckResult",
                        data: { supplierOrderID: row._id, retailBrandID: row.retailBrandID, storeID: row.storeID, statusOnly: true },
                        onErrorShowMsg: false, onError: () => {} });
                    if (active && response?.data?.status === 200 && response.data.data?.summary)
                        setCheckStates(previous => ({ ...previous, [row._id]: { summary: response.data.data.summary,
                            context: checkContextKey(row), listing: listingCheckKey(row) } }));
                    } catch { /* Keep the cycle guard until all selected rows settle. */ }
                }
            } finally { polling.current = false; }
        };
        const timer = setInterval(poll, 10000);
        return () => { active = false; clearInterval(timer); };
    }, []);

    useEffect(() => {
        if (usePremiumSummary) props?.onRowsLoaded?.((data?.pages || []).flatMap(page => page?.data?.result || [])
            .map(row => ({ ...row, automaticCheck: reconcileCheckState(row, checkStates[row._id]) })));
    }, [data?.pages, checkStates, props?.onRowsLoaded, usePremiumSummary]);

    //const totalCount = data?.pages[0]?.data?.totalCount;
    const nextDataCount = data?.pages[data?.pages.length - 1].data?.nextDataCount;
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        handleScroll();

        setminimumCardsFill(getTotalCards() + 1);
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages]);
    // #endregion

    // #region METHODS
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const scrolledRatio = scrollContainer.scrollTop / (scrollContainer.scrollHeight - scrollContainer.clientHeight);
        const isAtBottom = scrolledRatio >= 0.6;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    const getTotalCards = () => {
        return pageList?.reduce((acc, it) => {
            return acc + it?.data?.result?.length;
        }, 0);
    };

    const handleReset = infiniteGridQuerykey => {
        scrollContainerRef.current.scrollTop = 0;
        setScroll(true);
        AppQueryClient.clear();

        const newQueryKeyTMP = [...infiniteGridQuerykey];
        newQueryKeyTMP["refresh"] = uuidv4();
        setQueryKey(newQueryKeyTMP);
    };
    // #endregion

    useImperativeHandle(ref, () => ({
        reset: handleReset
    }));

    return (
        <StyledPage className={`library-scrollable ${props?.className || ""}`} ref={scrollContainerRef}>
            {isLoading && !isFetchingNextPage ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: pageLimit }, () => (
                        usePremiumSummary ? <PremiumOrdersGridCard key={uuidv4()} loading /> :
                            <CardGridSkeleton
                                key={uuidv4()}
                                height="436.28px"
                                minColumnWidth={props?.minColumnWidth || "395px"}
                                imageHeight="69px"
                            />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    {pageList?.map(it => {
                        return it?.data?.result?.map((item, i) => (
                            <OrderCard
                                key={item?.ourID}
                                image={item?.labelBrandLogo}
                                title={item?.orderNR}
                                id={item?.ourID}
                                isCardEnabled={item?.status?.toLowerCase() !== "suspended"}
                                isCardSuspended={item?.status?.toLowerCase() === "suspended"}
                                data={{ ...item, automaticCheck: reconcileCheckState(item, checkStates[item._id]) }}
                                cardData={{ ...item, automaticCheck: reconcileCheckState(item, checkStates[item._id]) }}
                                onDeleteClick={props?.onDeleteClick ? item => props?.onDeleteClick(item) : undefined}
                                onViewClick={props?.onViewClick ? item => props?.onViewClick(item) : undefined}
                                onExportClick={props?.onExportClick ? item => props?.onExportClick(item) : undefined}
                                warehouse={usePremiumSummary ? props?.destinationState?.({ ...item, automaticCheck: reconcileCheckState(item, checkStates[item._id]) }) : undefined}
                                onRulesClick={props?.onRulesClick ? item => props?.onRulesClick(item) : undefined}
                                onCheckClick={props?.onCheckClick}
                                manualReports={props?.manualReports}
                                isArchivePage={isArchivePage}
                            />
                        ));
                    })}

                    {!isFetchingNextPage &&
                        minimumCardsFill - getTotalCards() > 0 &&
                        Array.from({ length: minimumCardsFill - getTotalCards() }, (_, index) => (
                            <OrderCard
                                key={index}
                                isCardEnabled={false}
                                primaryButton={{ title: "VIEW" }}
                                secondaryButton={{ title: "Export to POS" }}
                            />
                        ))}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            usePremiumSummary ? <PremiumOrdersGridCard key={index} loading /> :
                                <CardGridSkeleton
                                    key={index}
                                    height="436.28px"
                                    minColumnWidth={props?.minColumnWidth || "395px"}
                                    imageHeight="69px"
                                />
                        ))}
                </div>
            )}
        </StyledPage>
    );
});
